import React, { useState } from 'react';
import { ListGroup, Badge, Button, Card, DropdownButton, ButtonGroup, Dropdown } from "react-bootstrap";
import {  useAddTemplateMutation, useUpdateTemplateMutation, useDeleteTemplateMutation, useGetAllTemplatesQuery } from '../../api/messageAPI';
import { createEmptyStoredMessageTemplate, MessageTemplate, StoredMessageTemplate } from '../../api/types/messages';
import { LoaderComponent } from '../LoaderComponent';
import { EditableMessage, MessagesEditor } from './MessageEditor';
import { Icon } from '../ui/Icon';
import { ConfirmButton } from '../utils/ConfirmButton';
import { UpdateResultComponent } from '../utils/UpdateResultComponent';
import { cloneDeep } from 'lodash';
import { useMessageBoilerplates } from '../../hooks/useMessageBoilerplate';
import { InternalLabelVariant, LanguageCodeVariant, MessageTypeVariant } from '../../constants';
import { keyBadge } from '../ui';

interface MessageAction {
    label: string;
    name: string;
    confirm?: string;
    icon?: string;
    variant?: string;
}

interface ActionHandler {
    onAction: (action: string, sm:MessageTemplate)=>void;
    actions: MessageAction[]
}

interface MessagesListProps {
    messages: StoredMessageTemplate[];
    collection: string;
    actions?: ActionHandler
    highlight?: StoredMessageTemplate; 
    onEdit?: (m: StoredMessageTemplate) => void;
    onCopy?: (m: StoredMessageTemplate)=>void;
    onDelete: (m:StoredMessageTemplate)=>void;
 }
 
 export const MessagesList: React.FC<MessagesListProps> = (props) => {
 
   const doAction = (name: string, m:MessageTemplate)=> {
      props.actions?.onAction(name, m);
   }

    const createAction = (m: MessageTemplate, action:MessageAction, index:number)=>{
         const variant = action.variant ?? 'info';
         if(action.confirm) {
           return <ConfirmButton key={index} size="sm" variant={variant} className='ms-5' onConfirm={()=> doAction(action.name, m)} text={action.confirm}>
                { action.icon ? <Icon name={action.icon}/> : ''} {action.label}
            </ConfirmButton> 
        }
        return <Button  key={index} size="sm" variant={variant} className='ms-5' onClick={()=> doAction(action.name, m)}>
        { action.icon ? <Icon name={action.icon}/> : ''} {action.label}
        </Button> ;
    }

    const item = (m: StoredMessageTemplate, index:number) => {
       const className = (props.highlight && props.highlight.id === m.id) ? 'bg-body-secondary' : '';
       return <ListGroup.Item key={index} className={className}>
          { keyBadge(m.id) }
          <Badge bg={MessageTypeVariant} className='me-1'>{m.messageType}</Badge>
          {m.studyKey && m.studyKey !== '' ? <Badge bg="warning" className='me-1'>{m.studyKey}</Badge> : ''}
          {m.internal_name && m.internal_name !== '' ? <Badge bg={InternalLabelVariant} className='me-1'>{m.internal_name}</Badge> : ''}
          
          {m.defaultLanguage ? <Badge bg={LanguageCodeVariant} className='me-1'>{m.defaultLanguage}</Badge> : ''}
          {props.onEdit ? <Button size="sm" onClick={() => {
             if (props.onEdit) props.onEdit(m)
          }}><Icon name="edit"/> Edit</Button> : ''}
          {props.onCopy ? <Button size="sm" className='ms-1' onClick={() => {
             if (props.onCopy) props.onCopy(m)
          }}> <Icon name="copy"/> Copy</Button> : ''}
          { props.actions ? props.actions.actions.map((action, index)=>createAction(m, action, index)) : ''}
          <ConfirmButton size="sm" variant="danger" className='ms-5' onConfirm={()=> props.onDelete(m)} text="Do you really want to remove this element"><Icon name="trash"/> Delete</ConfirmButton> 
       </ListGroup.Item>
    }
 
    return <ListGroup className='mt-2'>
             {props.messages.map(item)}
          </ListGroup>
 }
 
 type pageMode = 'list' | 'edit';

interface MessageTemplatesListEditorProps {
    collection: string;
    title?: string;
    allowedMessageTypes: string[]
    actions?: ActionHandler
    highlight?: StoredMessageTemplate; 
    
}
 
 export const MessageTemplatesListEditor: React.FC<MessageTemplatesListEditorProps> = (props) => {
 
    const { data, error, isLoading, refetch } = useGetAllTemplatesQuery(props.collection);

   const boilerplates = useMessageBoilerplates(props.collection);

    const [ mode, setMode ] = useState<pageMode>('list');
 
    const [editedMsg, setEditedMsg] = useState<StoredMessageTemplate | undefined>(undefined);
 
    const [addMsg, addResult] = useAddTemplateMutation();
 
    const [updateMsg, updateResult] = useUpdateTemplateMutation();
 
    const [deleteMsg, deleteResult] = useDeleteTemplateMutation();
 
    const handleEdit = (m: StoredMessageTemplate) => {
       setEditedMsg(m);
       setMode('edit');
    }
 
    const handleDelete = (m: StoredMessageTemplate) => {
       deleteMsg(m).then( (res) => {
          if('data' in res) {
             refetch();
          }
       });
    }

    const emtpyFactory = ()=>{
      return createEmptyStoredMessageTemplate(props.collection);
    }
 
    const handleOnSave = (edited: EditableMessage) => {
      const m = edited as StoredMessageTemplate;
      m.collection = props.collection;
      console.log('saveTemplate', m);
      if (m.id) {
          updateMsg(m)
       } else {
          addMsg(m);
       }
       setEditedMsg(undefined);
       refetch();
       setMode('list');
    };
 
    const handleOnCancelEdit = () => {
       setEditedMsg(undefined);
       setMode('list');
    };
 
    const handleRefresh = ()=>{
       refetch();
    }
 
    const handleAdd = ()=>{
       const m = createEmptyStoredMessageTemplate(props.collection);
       setEditedMsg(m);
       setMode('edit');
    }

    const handleCopy = (m:StoredMessageTemplate) => {
      const msg = cloneDeep(m);
      msg.id = undefined;
      setEditedMsg(msg);
      setMode('edit');
    }
 
    const addFromBoilerplate = (m: MessageTemplate) => {
      const msg: StoredMessageTemplate = { ...cloneDeep(m), collection: props.collection };
      msg.id = undefined;
      setEditedMsg(msg);
      setMode('edit');
    }

    return (
       <React.Fragment>
         { props.title ? <h3>props.title</h3> : ''}
          <LoaderComponent isLoading={isLoading} error={error} />
          <UpdateResultComponent result={updateResult} success="Message updated" error="Error during update of message" label="Update message" />
          <UpdateResultComponent result={addResult} success="Message added" error="Error during adding of message" label="Add message" />
          <UpdateResultComponent result={deleteResult} success="Message deleted" error="Error while removing message" label="Delete message"/>
          { data && mode === 'list' ? 
               <Card>
               <Card.Body>
                  <div className='mb-1'>
                     <Button onClick={handleRefresh} className="me-1" size="sm"><Icon name="sync" /> Refresh</Button>
                     <DropdownButton variant="primary"  size="sm" title={<span><Icon name="plus" /> Add </span>} as={ButtonGroup} className="me-1">
                        <Dropdown.Item key="add" onClick={handleAdd}>Empty message</Dropdown.Item>
                        <Dropdown.Divider/>
                        {  boilerplates.map((r, i) => <Dropdown.Item key={i} onClick={e=>addFromBoilerplate(r.template)}>{r.label}</Dropdown.Item>)}
                     </DropdownButton>
                  </div>
                  <MessagesList messages={data.templates} onEdit={handleEdit} key="list" onCopy={handleCopy} onDelete={handleDelete} collection={props.collection} actions={props.actions} highlight={props.highlight} /> 
               </Card.Body>
            </Card>
          : '' }
          { mode === 'edit' ? <MessagesEditor factory={emtpyFactory} mode="stored" onSave={handleOnSave} onCancel={handleOnCancelEdit} template={editedMsg} allowedMessageTypes={props.allowedMessageTypes} key="editor" /> : ''}
       </React.Fragment>
    )
 };